import { getTheme, IColumn, Text } from "@fluentui/react";

import TagPicture from "../components/ItemReplacement/Tag/TagPicture";
import { OverrideValue } from "../ItemsStep/ProductListItem/styles";

import { toLocaleString } from "@/utils/ToLocaleString";

const theme = getTheme();

type OnColumnClick = (
  event: React.MouseEvent<HTMLElement>,
  column: IColumn
) => void;

export function createTagColumns(onColumnClick: OnColumnClick): Array<IColumn> {
  return [
    {
      key: "tag",
      name: "Tag",
      fieldName: "inventory.serialNumber",
      minWidth: 300,
      maxWidth: 300,
      onColumnClick,
      onRender: item =>
        item.inventory
          .filter(iv => iv?.product?.category?.code === "TAG")
          .map(iv => (
            <TagPicture
              key={iv.id}
              serialNumber={iv.serialNumber}
              brandName={iv.product.brand?.name}
            />
          ))
    },
    {
      key: "vehicle",
      name: "Veículo",
      fieldName: "additionalInfo.vehicleLicensePlate",
      minWidth: 200,
      onColumnClick,
      isMultiline: true,
      isRowHeader: true,
      onRender: item => (
        <Text>
          <p style={{ marginBottom: 8 }}>
            <strong>Placa:</strong>{" "}
            {item.additionalInfo.vehicleLicensePlate.replace(
              /(.{3})(.)/,
              "$1-$2"
            )}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Tipo:</strong> {item.additionalInfo.vehicleType.description}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Centro de Distribuição:</strong>{" "}
            {item.inventory[0]?.distributionCenter?.name}
          </p>
        </Text>
      )
    },
    {
      key: "operation",
      name: "Operação",
      fieldName: "operation.name",
      minWidth: 120,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: item => item?.operation?.name
    },
    {
      key: "plan",
      name: "Plano de benefícios",
      fieldName: "offerPrice.offer.plan.name",
      minWidth: 120,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: item => item.offerPrice.offer.plan.name
    },
    {
      key: "monthlyPrice",
      name: "Valor do plano de benefícios",
      fieldName: "offerPrice.price",
      minWidth: 100,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: ({ offerPrice: { price } }) => `${toLocaleString(price)}/mês`
    },
    {
      key: "priceValue",
      name: "Valor da Tag",
      fieldName: "offerPrice.salesConditions.cashValue",
      minWidth: 80,
      onColumnClick,
      isRowHeader: true,
      onRender: ({ saleCondition }) => {
        if (saleCondition?.cashValue)
          return toLocaleString(saleCondition?.cashValue);

        return "-";
      }
    },
    {
      key: "canceledAtStartag",
      name: "Cancelado",
      fieldName: "canceledAtStartag",
      minWidth: 120,
      onColumnClick,
      styles: { root: {} },
      isRowHeader: false,
      isMultiline: true,
      onRender: item => {
        return item.inventory.map(value => {
          if (value?.canceledAtStartag) {
            const canceledAtStartag = new Intl.DateTimeFormat("pt-BR").format(
              new Date(value.canceledAtStartag)
            );

            return (
              <OverrideValue key={item.id} theme={theme}>
                {canceledAtStartag}
              </OverrideValue>
            );
          } else {
            return "-";
          }
        });
      }
    }
  ];
}

export function createCompactTagColumns(
  onColumnClick: OnColumnClick
): Array<IColumn> {
  return [
    {
      key: "tag",
      name: "Número de série",
      fieldName: "inventory.serialNumber",
      minWidth: 150,
      maxWidth: 150,
      onColumnClick,
      isRowHeader: true,
      onRender: item =>
        item.inventory
          .filter(iv => iv?.product?.category?.code === "TAG")
          .map(iv => iv.serialNumber.match(/.{1,4}/g).join(" "))
    },
    {
      key: "vehicleLicensePlate",
      name: "Placa do veículo",
      fieldName: "additionalInfo.vehicleLicensePlate",
      minWidth: 110,
      maxWidth: 110,
      onColumnClick,
      isRowHeader: true,
      onRender: item =>
        item.additionalInfo.vehicleLicensePlate.replace(/(.{3})(.)/, "$1-$2")
    },
    {
      key: "vehicleType",
      name: "Tipo de veículo",
      fieldName: "additionalInfo.vehicleType",
      minWidth: 250,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: item => item.additionalInfo.vehicleType.description
    },
    {
      key: "operation",
      name: "Operação",
      fieldName: "operation.name",
      minWidth: 120,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: item => item?.operation?.name
    },
    {
      key: "plan",
      name: "Plano de benefícios",
      fieldName: "offerPrice.offer.plan.name",
      minWidth: 120,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: item => item.offerPrice.offer.plan.name
    },
    {
      key: "monthlyPrice",
      name: "Valor do plano de benefícios",
      fieldName: "offerPrice.price",
      minWidth: 100,
      onColumnClick,
      isRowHeader: true,
      isMultiline: true,
      onRender: ({ offerPrice: { price } }) => `${toLocaleString(price)}/mês`
    },
    {
      key: "priceValue",
      name: "Valor da Tag",
      fieldName: "offerPrice.salesConditions.cashValue",
      minWidth: 80,
      isRowHeader: true,
      onColumnClick,
      onRender: ({ saleCondition }) => {
        if (saleCondition?.cashValue)
          return toLocaleString(saleCondition?.cashValue);

        return "-";
      }
    },
    {
      key: "canceledAtStartag",
      name: "Cancelado",
      fieldName: "canceledAtStartag",
      minWidth: 120,
      onColumnClick,
      styles: { root: {} },
      isRowHeader: false,
      isMultiline: true,
      onRender: item => {
        return item.inventory.map((value, index) => {
          const key = `${item.id}-${index}`;

          if (value.canceledAtStartag) {
            const canceledAtStartag = new Intl.DateTimeFormat("pt-BR").format(
              new Date(value.canceledAtStartag)
            );

            return (
              <OverrideValue key={key} theme={theme}>
                {canceledAtStartag}
              </OverrideValue>
            );
          } else {
            return <OverrideValue key={key}>-</OverrideValue>;
          }
        });
      }
    }
  ];
}
